import type { ReactNode } from "react";
import { useRef, createContext } from "react";
import type {
  ReferenceDataFamily,
  ReferenceDataMajor,
  ReferenceDataMenu,
  ReferenceDataMenuSection,
} from "@/pages/management/shared/endpoints/reference-data/inout/ReferenceDataOut.ts";
import type { ListQueryParams } from "@/pages/management/technicalsheets/list/api/dto/in/TechnicalSheetsListIn.ts";
import { SortOrder } from "@/shared/utils/SortOrder.ts";

export type FamiliesById = Map<number, ReferenceDataFamily>;
export type MajorsById = Map<number, ReferenceDataMajor>;
export type MenusById = Map<number, ReferenceDataMenu>;
export type SectionsById = Map<number, ReferenceDataMenuSection>;

export interface TechnicalSheetListContextProps {
  families: FamiliesById;
  setFamilies: (domainFamilies: ReferenceDataFamily[] | undefined) => void;
  majors: MajorsById;
  setMajors: (domainMajors: ReferenceDataMajor[] | undefined) => void;
  activeMenus: MenusById;
  setActiveMenus: (activeMenus: ReferenceDataMenu[] | undefined) => void;
  activeMenuSections: SectionsById;
  setActiveMenuSections: (
    activeMenuSections: ReferenceDataMenuSection[] | undefined,
  ) => void;
  getLastQueriedUrl: () => string;
  setLastQueriedUrl: (url: string) => void;
  getLastQueriedParams: () => ListQueryParams;
  setLastQueriedParams: (params: ListQueryParams) => void;
}

export const TechnicalSheetListContext = createContext<
  TechnicalSheetListContextProps | undefined
>(undefined);

export const TechnicalSheetListDataProvider: React.FC<{
  children: ReactNode;
}> = ({ children }: { children: ReactNode }) => {
  const families = new Map<number, ReferenceDataFamily>();
  const setFamilies = (
    domainFamilies: ReferenceDataFamily[] | undefined,
  ): void => {
    families.clear();
    domainFamilies?.forEach((family) => families.set(family.id, family));
  };

  const majors = new Map<number, ReferenceDataMajor>();
  const setMajors = (domainMajors: ReferenceDataMajor[] | undefined): void => {
    majors.clear();
    domainMajors?.forEach((major) => majors.set(major.id, major));
  };

  const activeMenus = new Map<number, ReferenceDataMenu>();
  const setActiveMenus = (
    domainMenus: ReferenceDataMenu[] | undefined,
  ): void => {
    activeMenus.clear();
    domainMenus?.forEach((domainMenu) => {
      activeMenus.set(domainMenu.id, domainMenu);
    });
  };

  const activeMenuSections = new Map<number, ReferenceDataMenuSection>();
  const setActiveMenuSections = (
    domainMenuSection: ReferenceDataMenuSection[] | undefined,
  ): void => {
    activeMenuSections.clear();
    domainMenuSection?.forEach((domainMenuSection) => {
      activeMenuSections.set(domainMenuSection.id, domainMenuSection);
    });
  };

  const lastQueriedUrl = useRef<string>("");
  const getLastQueriedUrl = (): string => lastQueriedUrl.current;
  const setLastQueriedUrl = (url: string): void => {
    lastQueriedUrl.current = url;
  };

  const lastQueriedParams = useRef<ListQueryParams>({
    size: 30,
    sort: SortOrder.CREATION_DATE_DESC,
  });
  const getLastQueriedParams = (): ListQueryParams => lastQueriedParams.current;
  const setLastQueriedParams = (params: ListQueryParams): void => {
    lastQueriedParams.current = params;
  };

  return (
    <TechnicalSheetListContext.Provider
      value={{
        families,
        setFamilies,
        majors,
        setMajors,
        activeMenus,
        setActiveMenus,
        activeMenuSections,
        setActiveMenuSections,
        getLastQueriedUrl,
        setLastQueriedUrl,
        getLastQueriedParams,
        setLastQueriedParams,
      }}
    >
      {children}
    </TechnicalSheetListContext.Provider>
  );
};
